import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { FaCloudUploadAlt, FaSearch, FaCopy, FaCheck, FaTrashAlt } from 'react-icons/fa';
import { useImages } from '../../hooks/useImages';

function ImageManager() {
  const { images, loading, uploadImage, deleteImage } = useImages();
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [copied, setCopied] = useState(null);

  const onDrop = useCallback(async (acceptedFiles) => {
    setUploading(true);
    for (const file of acceptedFiles) {
      try {
        await uploadImage(file);
      } catch {
        alert(`Erreur lors de l'upload de ${file.name}`);
      }
    }
    setUploading(false);
  }, [uploadImage]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'] },
    multiple: true
  });

  const handleDelete = async (img) => {
    if (!window.confirm(`Supprimer ${img.name} ?`)) return;
    try {
      await deleteImage(img.path);
    } catch {
      alert('Erreur lors de la suppression');
    }
  };

  const copyPath = (path) => {
    navigator.clipboard.writeText(path);
    setCopied(path);
    setTimeout(() => setCopied(null), 2000);
  };

  const filtered = images.filter(img =>
    img.name.toLowerCase().includes(search.toLowerCase()) ||
    img.path.toLowerCase().includes(search.toLowerCase())
  );

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div>
      <div className="admin-page-header">
        <h1>Images</h1>
        <span style={{ color: '#888', fontSize: '14px' }}>
          {filtered.length} image{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      <div
        {...getRootProps()}
        className={`admin-dropzone${isDragActive ? ' active' : ''}`}
      >
        <input {...getInputProps()} />
        <div className="dropzone-icon">
          <FaCloudUploadAlt />
        </div>
        {uploading ? (
          <p style={{ color: '#32afe5', fontWeight: '500' }}>Upload en cours...</p>
        ) : isDragActive ? (
          <p style={{ color: '#32afe5', fontWeight: '500' }}>Relâchez pour uploader...</p>
        ) : (
          <div>
            <p style={{ color: '#555', marginBottom: '6px', fontWeight: '500' }}>
              Glissez-déposez des images ici
            </p>
            <p style={{ color: '#aaa', fontSize: '13px' }}>
              ou cliquez pour sélectionner — JPG, PNG, GIF, WebP, SVG (max 10 MB)
            </p>
          </div>
        )}
      </div>

      <div className="admin-search-wrapper">
        <FaSearch />
        <input
          type="text"
          placeholder="Rechercher par nom..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="admin-search"
        />
      </div>

      {loading ? (
        <p style={{ color: '#888', textAlign: 'center', padding: '40px' }}>Chargement...</p>
      ) : (
        <div className="admin-image-grid">
          {filtered.map(img => (
            <div key={img.path} className="admin-image-card">
              <div className="image-preview">
                <img src={img.path} alt={img.name} loading="lazy" />
              </div>
              <div className="image-info">
                <div style={{
                  fontSize: '13px', color: '#333', overflow: 'hidden',
                  textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: '4px',
                  fontWeight: '500'
                }} title={img.name}>
                  {img.name}
                </div>
                <div style={{ fontSize: '12px', color: '#aaa', marginBottom: '10px' }}>
                  {formatSize(img.size)}
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button
                    onClick={() => copyPath(img.path)}
                    className="admin-btn admin-btn-secondary"
                    style={{
                      flex: 1, padding: '5px 8px', fontSize: '12px', justifyContent: 'center',
                      background: copied === img.path ? '#f0fdf4' : undefined,
                      color: copied === img.path ? '#16a34a' : undefined
                    }}
                  >
                    {copied === img.path ? <><FaCheck /> Copié</> : <><FaCopy /> Chemin</>}
                  </button>
                  <button
                    onClick={() => handleDelete(img)}
                    className="admin-btn admin-btn-danger"
                    style={{ padding: '5px 8px', fontSize: '12px' }}
                  >
                    <FaTrashAlt />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ImageManager;
